import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllGrammarFillinExercises,
  updateGrammarFillinExercise,
  createGrammarFillinExercise,
  deleteGrammarFillinExercise
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';

const ROLES = ['S', 'V', 'O'];

const ROLE_COLORS = {
  S: { bg: '#dbeafe', border: '#93c5fd', label: 'Subject' },
  V: { bg: '#fef3c7', border: '#fcd34d', label: 'Verb' },
  O: { bg: '#d1fae5', border: '#6ee7b7', label: 'Object' },
};

const SvoLangEditor = ({ label, items, onChange }) => {
  const handleChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...items, { text: '', role: 'S' }]);
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div style={{
      marginBottom: '10px',
      padding: '8px 10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e9ecef',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '6px',
      }}>
        <span style={{
          fontWeight: 'bold', fontSize: '12px', color: '#495057',
          padding: '2px 8px', backgroundColor: '#e9ecef', borderRadius: '4px',
        }}>{label}</span>
        <button
          onClick={handleAdd}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#16a34a'; e.currentTarget.style.transform = 'translateY(0)'; }}
          style={{
            padding: '4px 12px', backgroundColor: '#16a34a', color: 'white',
            border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px',
            fontWeight: '500', transition: 'all 0.15s ease',
          }}
        >+ Pridat</button>
      </div>
      {items.map((item, idx) => {
        const roleColor = ROLE_COLORS[item.role] || ROLE_COLORS.S;
        return (
          <div key={idx} style={{
            display: 'flex', gap: '6px', marginBottom: '5px', alignItems: 'center',
          }}>
            <select
              value={item.role || 'S'}
              onChange={(e) => handleChange(idx, 'role', e.target.value)}
              onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
              onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
              style={{
                width: '55px', padding: '6px 4px', fontSize: '12px', fontWeight: 'bold',
                border: `2px solid ${roleColor.border}`, borderRadius: '6px',
                backgroundColor: roleColor.bg, cursor: 'pointer', textAlign: 'center',
                transition: 'box-shadow 0.15s ease', outline: 'none',
              }}
            >
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input
              type="text"
              value={item.text || ''}
              onChange={(e) => handleChange(idx, 'text', e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              placeholder={`${ROLE_COLORS[item.role]?.label || 'text'}...`}
              style={{
                flex: 1, padding: '8px 12px', fontSize: '13px',
                border: '1.5px solid #e5e7eb', borderRadius: '6px',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease', outline: 'none',
              }}
            />
            <button
              onClick={() => handleRemove(idx)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#dc2626'; }}
              style={{
                width: '28px', height: '28px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', backgroundColor: 'transparent', color: '#dc2626',
                border: '1.5px solid #dc2626', borderRadius: '6px', cursor: 'pointer',
                fontSize: '12px', fontWeight: 'bold', lineHeight: 1,
                transition: 'all 0.15s ease',
              }}
            >X</button>
          </div>
        );
      })}
      {items.length === 0 && (
        <div style={{ color: '#999', fontSize: '12px', fontStyle: 'italic', padding: '4px 0' }}>
          Prazdne - klikni "+ Pridat"
        </div>
      )}
    </div>
  );
};

const SvoAnalysisEditor = ({ value, onChange }) => {
  const svo = value || { en: [], cs: [] };

  const handleLangChange = (lang, items) => {
    onChange({ ...svo, [lang]: items });
  };

  return (
    <div style={{ minWidth: '280px' }}>
      <SvoLangEditor label="EN" items={svo.en || []} onChange={(items) => handleLangChange('en', items)} />
      <SvoLangEditor label="CS" items={svo.cs || []} onChange={(items) => handleLangChange('cs', items)} />
    </div>
  );
};

const renderSvoDisplay = (value) => {
  if (!value) return '-';
  const renderLang = (items, label) => {
    if (!items || items.length === 0) return null;
    return (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px', flexWrap: 'wrap' }}>
        <span style={{
          fontSize: '10px', fontWeight: 'bold', color: '#6c757d',
          padding: '1px 5px', backgroundColor: '#e9ecef', borderRadius: '3px',
          minWidth: '22px', textAlign: 'center',
        }}>{label}</span>
        {items.map((item, i) => {
          const roleColor = ROLE_COLORS[item.role] || ROLE_COLORS.S;
          return (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              padding: '2px 6px', fontSize: '11px', borderRadius: '4px',
              backgroundColor: roleColor.bg, border: `1px solid ${roleColor.border}`,
            }}>
              <strong style={{ fontSize: '10px' }}>{item.role}</strong>
              <span>{item.text}</span>
            </span>
          );
        })}
      </div>
    );
  };
  return (
    <div>
      {renderLang(value.en, 'EN')}
      {renderLang(value.cs, 'CS')}
    </div>
  );
};

const GrammarFillinManager = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetchAllGrammarFillinExercises();
      setExercises(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching grammar fill-in exercises:', err);
      setError('Failed to load grammar fill-in exercises');
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(exercises.map((item) => item.topic))].filter(Boolean);
  }, [exercises]);

  const handleSave = async (id, data) => {
    await updateGrammarFillinExercise(id, data);
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...data } : ex)));
  };

  const handleCreate = async (data) => {
    const newItem = await createGrammarFillinExercise(data);
    setExercises((prev) => [...prev, newItem]);
  };

  const handleDelete = async (id) => {
    await deleteGrammarFillinExercise(id);
    setExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 120 },
    { key: 'sentence', header: 'Sentence', editable: true, width: 300 },
    { key: 'correct_answer', header: 'Correct Answer', editable: true, width: 120 },
    { key: 'blank_position', header: 'Blank Pos', editable: true, width: 80 },
    { key: 'exercise_order', header: 'Order', editable: true, width: 70 },
    { key: 'grammar_note', header: 'Grammar Note', editable: true, width: 200 },
    { key: 'czech_sentence', header: 'Czech Sentence', editable: true, width: 250 },
    { key: 'svo_analysis', header: 'SVO Analysis', editable: true, width: 300,
      render: renderSvoDisplay,
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'text', label: 'Topic', required: true },
    { key: 'sentence', type: 'textarea', label: 'Sentence', minHeight: '60px', required: true },
    { key: 'correct_answer', type: 'text', label: 'Correct Answer', required: true },
    { key: 'blank_position', type: 'number', label: 'Blank Position', default: 1 },
    { key: 'exercise_order', type: 'number', label: 'Order', default: 1 },
    { key: 'grammar_note', type: 'textarea', label: 'Grammar Note', minHeight: '60px' },
    { key: 'czech_sentence', type: 'textarea', label: 'Czech Sentence', minHeight: '60px' },
    {
      key: 'svo_analysis',
      type: 'custom',
      label: 'SVO Analysis',
      default: { en: [], cs: [] },
      renderEdit: (value, onChange) => <SvoAnalysisEditor value={value} onChange={onChange} />,
    },
  ];

  if (loading && exercises.length === 0) {
    return <Loading />;
  }

  if (error && exercises.length === 0) {
    return <Error message={error} />;
  }

  return (
    <AdminTable
      data={exercises}
      columns={columns}
      onSave={handleSave}
      onCreate={handleCreate}
      onDelete={handleDelete}
      editableFields={editableFields}
      title="Grammar Fill-in Exercises"
      filterField="topic"
      uniqueFilterValues={uniqueTopics}
      loading={loading}
      emptyMessage="No grammar fill-in exercises found."
    />
  );
};

export default GrammarFillinManager;
